export function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let str = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    str += String.fromCharCode(bytes[i])
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function base64urlToBuffer(base64url: string): ArrayBuffer {
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  while (base64.length % 4) {
    base64 += '='
  }
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

export function isWebAuthnSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined'
}

export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false
  try {
    const pkc = window.PublicKeyCredential as any
    if (typeof pkc.getClientCapabilities === 'function') {
      const caps = await pkc.getClientCapabilities()
      if (typeof caps?.passkeyPlatformAuthenticator === 'boolean') {
        return caps.passkeyPlatformAuthenticator
      }
    }
    if (typeof pkc.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
      return await pkc.isUserVerifyingPlatformAuthenticatorAvailable()
    }
  } catch {
    // ignore
  }
  return true
}

export function prepareRequestOptions(options: any): PublicKeyCredentialRequestOptions {
  const pkc = window.PublicKeyCredential as any
  if (typeof pkc?.parseRequestOptionsFromJSON === 'function') {
    try {
      return pkc.parseRequestOptionsFromJSON(options)
    } catch {
      // Fallback to manual ArrayBuffer conversion below
    }
  }
  const prepared: any = {
    ...options,
    challenge: base64urlToBuffer(options.challenge)
  }
  if (Array.isArray(options.allowCredentials)) {
    prepared.allowCredentials = options.allowCredentials.map((cred: any) => ({
      ...cred,
      id: base64urlToBuffer(cred.id)
    }))
  }
  return prepared
}

export function prepareCreationOptions(options: any): PublicKeyCredentialCreationOptions {
  const pkc = window.PublicKeyCredential as any
  if (typeof pkc?.parseCreationOptionsFromJSON === 'function') {
    try {
      return pkc.parseCreationOptionsFromJSON(options)
    } catch {
      // Fallback to manual ArrayBuffer conversion below
    }
  }
  const prepared: any = {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64urlToBuffer(options.user.id)
    }
  }
  if (Array.isArray(options.excludeCredentials)) {
    prepared.excludeCredentials = options.excludeCredentials.map((cred: any) => ({
      ...cred,
      id: base64urlToBuffer(cred.id)
    }))
  }
  return prepared
}

export function serializeAssertionCredential(credential: PublicKeyCredential): any {
  const anyCred = credential as any
  if (typeof anyCred.toJSON === 'function') {
    try {
      return anyCred.toJSON()
    } catch {
      // Fallback to manual serialization
    }
  }
  const response = credential.response as AuthenticatorAssertionResponse
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bufferToBase64url(response.authenticatorData),
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
      signature: bufferToBase64url(response.signature),
      userHandle: response.userHandle ? bufferToBase64url(response.userHandle) : null
    }
  }
}

export function serializeAttestationCredential(credential: PublicKeyCredential): any {
  const anyCred = credential as any
  if (typeof anyCred.toJSON === 'function') {
    try {
      return anyCred.toJSON()
    } catch {
      // Fallback to manual serialization
    }
  }
  const response = credential.response as AuthenticatorAttestationResponse
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToBase64url(response.attestationObject),
      clientDataJSON: bufferToBase64url(response.clientDataJSON)
    }
  }
}

export async function signalUnknownCredentialIfSupported(rpId: string, credentialId: string): Promise<void> {
  if (!isWebAuthnSupported() || !rpId || !credentialId) return
  const pkc = window.PublicKeyCredential as any
  if (typeof pkc.signalUnknownCredential === 'function') {
    try {
      await pkc.signalUnknownCredential({ rpId, credentialId })
    } catch {
      // Ignore Signal API errors
    }
  }
}

export async function signalAllAcceptedCredentialsIfSupported(
  rpId: string,
  userId: string,
  credentialIds: string[]
): Promise<void> {
  if (!isWebAuthnSupported() || !rpId || !userId) return
  const pkc = window.PublicKeyCredential as any
  if (typeof pkc.signalAllAcceptedCredentials === 'function') {
    try {
      await pkc.signalAllAcceptedCredentials({
        rpId,
        userId,
        allAcceptedCredentialIds: credentialIds
      })
    } catch {
      // Ignore Signal API errors
    }
  }
}

